import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { chamadosService } from "../../../lib/api";
import type { ChamadoKanban, FiltrosAPI } from "../../../types/api";

export const useChamadosKanban = (filtros?: FiltrosAPI) => {
  return useQuery({
    queryKey: ["chamados", "kanban", filtros],
    queryFn: () => chamadosService.getKanban(filtros),
    // A consulta do quadro calcula SLA item a item e é a mais cara da API.
    // Um minuto de validade evita refazê-la a cada troca de filtro, e não
    // refazer ao focar a janela evita uma rodada inteira só por alternar de
    // aba no navegador. O botão de atualizar continua disponível.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
};

export const useChamadoById = (id: number | null) => {
  return useQuery({
    queryKey: ["chamado", id],
    queryFn: () => chamadosService.getById(id!),
    enabled: !!id,
  });
};

export const useUpdatePrioridade = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      prioridade,
    }: {
      id: number;
      prioridade: number | null;
    }) => chamadosService.updatePrioridade(id, prioridade),
    onMutate: async ({ id, prioridade }) => {
      // Cancelar queries em andamento para evitar sobrescrita
      await queryClient.cancelQueries({ queryKey: ["chamados", "kanban"] });

      // Snapshot do cache atual
      const previousData = queryClient.getQueriesData<ChamadoKanban[]>({
        queryKey: ["chamados", "kanban"],
      });

      // Atualizar otimisticamente todas as queries do kanban
      queryClient.setQueriesData<ChamadoKanban[]>(
        { queryKey: ["chamados", "kanban"] },
        (old) =>
          old?.map((c) => (c.id === id ? { ...c, prioridade } : c)),
      );

      return { previousData };
    },
    onError: (_err, _vars, context) => {
      // Reverter em caso de erro
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["chamados", "kanban"] });
    },
  });
};

// export const useChamadoSla = (id: number) => {
//   return useQuery({
//     queryKey: ['chamado', id, 'sla'],
//     queryFn: () => chamadosService.getSla(id),
//     enabled: !!id,
//   });
// };

// export const useChamadoHistorico = (id: number) => {
//   return useQuery({
//     queryKey: ['chamado', id, 'historico'],
//     queryFn: () => chamadosService.getHistorico(id),
//     enabled: !!id,
//   });
// };
